import "core-js/es6";
import React from "react";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt-br";
import ReactDOM from "react-dom";
import { createStore } from "redux";
import { Provider } from "react-redux";
import { IntlProvider, updateIntl } from "react-intl-redux";
import { version as packageVersion } from "../package.json";

import Mediator from "./services/mediator";
import ChatService from "./services/chat";

import reducers from "./store/reducers";
import { toggleChatList } from "./store/actions/chat-list";
import { collapseAllChats } from "./store/actions/chats";
import {
  apiSetContextFilter,
  apiSetContextList,
  apiRefresh,
} from "./store/actions/api";

import { pt } from "./i18n/pt";
import { en } from "./i18n/en";
import { es } from "./i18n/es";

import App from "./App";

import "./index.css";

const initialState = {
  intl: {
    locale: "pt",
    messages: pt,
  },
};

const locales = {
  pt: pt,
  en: en,
  es: es,
};

moment.locale("pt-br");

const store = createStore(reducers, initialState);

const Social = (() => {
  const version = packageVersion;

  const defaultConfig = {
    id: "social",

    parentElement: "",

    i18n: {
      lang: "pt",
    },

    /* Seção destinada a configurações de APIs a serem utilizadas*/

    api: {
      application: "lecom-bpm",
      apiURL: "http://rush.dev.local/social-service/api",
      apiUsersURL: "http://rush.dev.local:8181/admin/api/v1/users",
      token: "385c5444-f0f6-4968-9623-4f6c55da118c",
      wsURL: "ws://rush.dev.local/social-service/websocket/lecom-bpm/",
    },

    /* Seção destinada a configurações de chat. Algumas regras de negócio são definidas aqui*/

    chat: {
      chatsPageSize: 20,
      descriptionMaxLength: 255,
      messageMaxLength: 3800,
      showDescription: true,
      emptyDescription: "Workspace", // o que aparecerá na descrição caso não haja um processo vinculado
      defaultCategory: "#Geral", // o que aparecerá como item selecionado padrão (caso não haja um processo vinculado)
      defaultPrivacy: "DESTINATARIOS", // qual é a privacidade padrão
      defaultCategoryPrivacyLock: true, // se a privacidade não pode ser alterada caso não haja um processo vinculado
      privacyElement: "message_visibility", // propriedade em "elements" de um chat que descreve a privacidade de um processo
      hashElementPrefix: "process_instance", // propriedade em "elements" de um chat que dita o elemento que será exibido na descrição de um chat
      hashElementInQuotes: false, // se o elemento a ser exibido na hashtag # exige aspas (caso o elemento tenha espaços, por exemplo)
      chatDescriptionElements: ["process_instance", "activity_instance"], // propriedades de "elements" de um chat que aparecerão na descrição do chat, nesta ordem
      chatRecipientsUsernamePath: ["user", "username"], // caminho para exibição de nomes de participantes do chat, nesta ordem
      allowFlags: false, // habilitar marcações (como as Tarefas), exige configuração de config.chatFlags
      emptyFlag: "chat.noFlag", // i18n para chat sem marcador
    },

    /* Seção destinada a configurações de marcadores. Os marcadores são salvos no backend, mas para isto, devem estar na config previamente. */

    chatFlags: [
      {
        name: "Tarefa",
        value: "TASK",
        color: "#FFD39A",
        style: {
          backgroundColor: "#FFD39A",
          color: "rgba(0, 0, 0, 0.45)",
          border: "none",
        },
        alternativeArchiveText: "chat.flagFinalize",
        alternativeArchivedPrefix: "chat.finalizedPrefix",
      },
    ],

    chatEvents: [],

    /* Seção destinada aos filtros dinâmicos. Todos os filtros do Social (com exceção de filtros por períoso, por processo e por participantes) são definidos abaixo.
       O nome dos filtros e seu sufixo para exibição em tooltips são traduzidos pelo i18n. Os parâmetros de busca deverão sempre ser enviados pelo campo params.
       Existem variáveis que o Social traduz para que a requisição seja feita, como %CURRENT_USER%
    */

    filter: [
      {
        index: 0,
        description: "filter.allActiveMessages",
        sufixForLabels: "filter.allActiveMessages.sufixForLabels",
        refreshOnNewMessages: false, // caso seja veradeira, ao receber novas mensagens e o filtro atual seja este filtro, o Social irá atualizar a lista de chats, e não aloca-lo dinamicamente (flutuação de chats) na lista de chats
        params: {
          archived: false,
        },
      },
      {
        index: 1,
        description: "filter.archivedMessages",
        sufixForLabels: "filter.archivedMessages.sufixForLabels",
        refreshOnNewMessages: true,
        params: {
          archived: true,
        },
      },
      {
        index: 2,
        description: "filter.allMessages",
        sufixForLabels: "filter.allMessages.sufixForLabels",
        refreshOnNewMessages: false,
        params: {},
      },
      {
        index: 3,
        description: "filter.messagesOpenedByMe",
        sufixForLabels: "filter.messagesOpenedByMe.sufixForLabels",
        refreshOnNewMessages: true,
        params: {
          createdBy: "%CURRENT_USER%",
        },
      },
    ],

    /* Seção destinada aos tipos de privacidades. */

    privacyTypes: [
      {
        value: "DESTINATARIOS",
        title: "privacy.private",
        description: "privacy.private.description",
        isDefault: true, // se é a privacidade padrão
        hiddenOnDescription: true, // se a privacidade ficará escondida na descrição
        onlyCurrentUsersOnMention: false, // true - se só será possível mencionar pessoas que já participam da conversa; false - se será permitido pesquisar no backend para mencionar pessoas dentro e fora do chat
      },
      {
        value: "PUBLICA",
        title: "privacy.public",
        description: "privacy.public.description",
        onlyCurrentUsersOnMention: false,
      },
      {
        value: "PARTICIPANTES",
        title: "privacy.participants",
        description: "privacy.participants.description",
        onlyCurrentUsersOnMention: false,
      },
    ],

    /* Seção usada para configurações gerais */

    setting: {
      userSimulation: true, // se existe modo simulação no sistema
      userSimulationPath: ["testUser", "username"], // se existe o modo simulação, o caminho para encontrar o usuário atual na rota de current user
    },

    /* Seção usada para configurações de temas */

    theme: {
      minimalist: true, // Estilo minimalista para main container colapsado
      socialButton: false, // Botão para abrir e fechar social
      socialFloatButton: true, // Main-container como botão flutuante
    },
  };

  let config = {};
  let instance = null;

  function init(currentConfig) {
    config = mergeConfig(currentConfig);

    if (document.getElementById(config.id)) {
      console.warn(`O container ${config.id} já está sendo utilizado`);
      return false;
    }

    if (config.i18n.lang !== defaultConfig.i18n.lang) {
      setLang(config.i18n.lang, config.i18n.messages);
    }

    instance = document.createElement("div");
    instance.id = config.id;

    let parent = null;

    if (config.parentElement) {
      parent = document.querySelector(config.parentElement);
    }

    if (parent) {
      parent.appendChild(instance);
    } else {
      document.body.appendChild(instance);
    }

    ReactDOM.render(
      <Provider store={store}>
        <IntlProvider>
          <App api={config.api} />
        </IntlProvider>
      </Provider>,
      instance
    );
  }

  function mergeConfig(currentConfig = {}) {
    let newConfig = {};

    newConfig.id = defaultConfig.id;

    newConfig.parentElement =
      currentConfig.parentElement || defaultConfig.parentElement;

    newConfig.api = {
      ...defaultConfig.api,
      ...currentConfig.api,
    };

    newConfig.chat = {
      ...defaultConfig.chat,
      ...currentConfig.chat,
    };

    newConfig.privacyTypes =
      currentConfig.privacyTypes || defaultConfig.privacyTypes;

    newConfig.i18n = {
      ...defaultConfig.i18n,
      ...currentConfig.i18n,
    };

    newConfig.setting = {
      ...defaultConfig.setting,
      ...currentConfig.setting,
    };

    newConfig.filter = currentConfig.filter || defaultConfig.filter;

    newConfig.theme = {
      ...defaultConfig.theme,
      ...currentConfig.theme,
    };

    newConfig.chatEvents = {
      ...defaultConfig.chatEvents,
      ...currentConfig.chatEvents,
    };

    newConfig.chatFlags = {
      ...defaultConfig.chatFlags,
      ...currentConfig.chatFlags,
    };

    return newConfig;
  }

  function setContextFilter(contextFilter) {
    store.dispatch(apiSetContextFilter(contextFilter));
  }

  function setContextList(contextList) {
    store.dispatch(apiSetContextList(contextList));
  }

  function setLang(lang, messages) {
    store.dispatch(
      updateIntl({ locale: lang, messages: messages || locales[lang] })
    );
    if (lang === "pt") moment.locale("pt-br");
    else if (lang === "en") moment.locale("en");
    else if (lang === "es") moment.locale("es");
  }

  function refresh() {
    store.dispatch(apiRefresh(true));
  }

  function show() {
    if (instance && instance.id) {
      let element = document.getElementById(instance.id);

      if (element) {
        element.style.display = "block";
      }
    }
  }

  function hide() {
    if (instance && instance.id) {
      let element = document.getElementById(instance.id);

      if (element) {
        element.style.display = "none";
      }
    }
  }

  function collapseAllWindow() {
    store.dispatch(toggleChatList(true));
    store.dispatch(collapseAllChats());
  }

  function maximizeChatList() {
    show();

    store.dispatch(toggleChatList(false));
  }

  function minimizeChatList() {
    store.dispatch(toggleChatList(true));
  }

  function toggleChatListBox() {
    const state = store.getState();
    const { chatsListOpened } = state.ChatList || {};

    store.dispatch(toggleChatList(!chatsListOpened));
  }

  function getConfig() {
    return config;
  }

  function getApiConfig() {
    return config && config.api;
  }

  function getChatConfig() {
    return config && config.chat;
  }

  function getFilterConfig() {
    return config && config.filter;
  }

  function getPrivacyTypesConfig() {
    return config && config.privacyTypes;
  }

  function getChatEventsConfig() {
    return config && config.chatEvents;
  }

  function getChatFlags() {
    return config && config.chatFlags;
  }

  function getVersion() {
    return version;
  }

  function getThemeConfig() {
    return config && config.theme;
  }

  function getSettingsConfig() {
    return config && config.setting;
  }

  function destroy() {
    if (instance && instance.id) {
      let social = document.getElementById(instance.id);

      if (social) {
        social.parentElement.removeChild(social);
      }
    }
  }

  async function hasProcessChat(processInstanceId) {
    try {
      const params = {
        element: processInstanceId,
        size: 1,
      };

      const { data } = await ChatService.getChats(params);

      return data?.content?.length > 0;
    } catch (error) {
      return false;
    }
  }

  return {
    version: packageVersion,
    init: init,
    setContextFilter: setContextFilter,
    setContextList: setContextList,
    setLang: setLang,
    getConfig: getConfig,
    getApiConfig: getApiConfig,
    getChatConfig: getChatConfig,
    getFilterConfig: getFilterConfig,
    getPrivacyTypesConfig: getPrivacyTypesConfig,
    getChatEventsConfig: getChatEventsConfig,
    getThemeConfig: getThemeConfig,
    getSettingsConfig: getSettingsConfig,
    refresh: refresh,
    getChatFlags: getChatFlags,
    getVersion: getVersion,
    show: show,
    hide: hide,
    maximizeChatList,
    minimizeChatList,
    collapseAllWindow: collapseAllWindow,
    toggleChatListBox: toggleChatListBox,
    on: Mediator.on,
    off: Mediator.off,
    emit: Mediator.emit,
    hasProcessChat,
    destroy: destroy,
  };
})();

window.Social = Social;
// Social.init()
